import { useState, useMemo } from 'react';
import { Copy, ArrowLeftRight } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { copyToClipboard } from '@/lib/utils';
import { toast } from 'sonner';

/* ---- minimal YAML serializer / parser (good for flat object trees) ---- */

function jsonToYaml(obj: unknown, indent = 0): string {
  const pad = '  '.repeat(indent);
  if (obj === null) return 'null';
  if (typeof obj === 'string') {
    if (/[:#\-{}\[\]\n]/.test(obj) || obj.trim() !== obj) return JSON.stringify(obj);
    return obj;
  }
  if (typeof obj === 'number' || typeof obj === 'boolean') return String(obj);
  if (Array.isArray(obj)) {
    if (obj.length === 0) return '[]';
    return obj
      .map((v) => {
        const child = jsonToYaml(v, indent + 1);
        if (typeof v === 'object' && v !== null) {
          return `${pad}-\n${child.replace(/^/gm, pad + '  ')}`;
        }
        return `${pad}- ${child}`;
      })
      .join('\n');
  }
  if (typeof obj === 'object') {
    const entries = Object.entries(obj as Record<string, unknown>);
    if (entries.length === 0) return '{}';
    return entries
      .map(([k, v]) => {
        if (typeof v === 'object' && v !== null) {
          return `${pad}${k}:\n${jsonToYaml(v, indent + 1)}`;
        }
        return `${pad}${k}: ${jsonToYaml(v, 0)}`;
      })
      .join('\n');
  }
  return '';
}

function yamlToJson(text: string): unknown {
  const lines = text.split('\n').filter((l) => l.trim() && !l.trim().startsWith('#'));
  const stack: { indent: number; node: unknown; key?: string }[] = [{ indent: -1, node: {} }];

  function parseScalar(s: string): unknown {
    s = s.trim();
    if (s === 'null' || s === '~') return null;
    if (s === 'true') return true;
    if (s === 'false') return false;
    if (/^-?\d+$/.test(s)) return parseInt(s, 10);
    if (/^-?\d*\.\d+$/.test(s)) return parseFloat(s);
    if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
      try {
        return JSON.parse(s.replace(/'/g, '"'));
      } catch {
        return s.slice(1, -1);
      }
    }
    return s;
  }

  for (const line of lines) {
    const indent = line.match(/^( *)/)?.[1].length ?? 0;
    const trimmed = line.trim();
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) stack.pop();
    const parent = stack[stack.length - 1].node as Record<string, unknown> | unknown[];

    if (trimmed.startsWith('- ')) {
      const rest = trimmed.slice(2).trim();
      if (!Array.isArray(parent)) {
        // Convert parent to array
        if (stack.length > 1) {
          const grand = stack[stack.length - 2].node as Record<string, unknown>;
          const key = stack[stack.length - 1].key!;
          grand[key] = [];
          stack[stack.length - 1].node = grand[key];
        }
      }
      const arr = stack[stack.length - 1].node as unknown[];
      if (rest.includes(':')) {
        const obj: Record<string, unknown> = {};
        const [k, ...vrest] = rest.split(':');
        const v = vrest.join(':').trim();
        if (v) obj[k.trim()] = parseScalar(v);
        arr.push(obj);
        stack.push({ indent, node: obj });
      } else {
        arr.push(parseScalar(rest));
      }
      continue;
    }

    const colon = trimmed.indexOf(':');
    if (colon === -1) continue;
    const key = trimmed.slice(0, colon).trim();
    const valuePart = trimmed.slice(colon + 1).trim();
    const obj = parent as Record<string, unknown>;
    if (!valuePart) {
      const child = {};
      obj[key] = child;
      stack.push({ indent, node: child, key });
    } else {
      obj[key] = parseScalar(valuePart);
    }
  }

  return stack[0].node;
}

const SAMPLE_YAML = `name: Utility OS
version: 1.0
features:
  - merge-pdf
  - image-resize
  - color-picker
metadata:
  local: true
  tools: 60`;

export function YamlConvert() {
  const [direction, setDirection] = useState<'y2j' | 'j2y'>('y2j');
  const [input, setInput] = useState(SAMPLE_YAML);

  const result = useMemo(() => {
    if (!input.trim()) return { ok: true, output: '', err: null };
    try {
      if (direction === 'y2j') {
        const obj = yamlToJson(input);
        return { ok: true, output: JSON.stringify(obj, null, 2), err: null };
      }
      const obj = JSON.parse(input);
      return { ok: true, output: jsonToYaml(obj), err: null };
    } catch (e) {
      return { ok: false, output: '', err: e instanceof Error ? e.message : 'Conversion error' };
    }
  }, [input, direction]);

  return (
    <div>
      <ToolHeader
        toolId="yaml-convert"
        title="YAML ↔ JSON"
        description="Bidirectional conversion. Supports nested objects, arrays, scalars."
      />
      <div className="flex items-center justify-between mb-4">
        <Tabs value={direction} onValueChange={(v) => {
          setDirection(v as typeof direction);
          setInput(v === 'y2j' ? SAMPLE_YAML : '{\n  "name": "Utility OS",\n  "tools": 60\n}');
        }}>
          <TabsList>
            <TabsTrigger value="y2j">YAML → JSON</TabsTrigger>
            <TabsTrigger value="j2y">JSON → YAML</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDirection((d) => (d === 'y2j' ? 'j2y' : 'y2j'));
            setInput(result.output || input);
          }}
        >
          <ArrowLeftRight size={11} />
          Swap
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border text-xs font-medium text-fg-muted">
            {direction === 'y2j' ? 'YAML' : 'JSON'}
          </div>
          <Textarea
            mono
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="!border-0 !rounded-t-none min-h-[400px] !bg-transparent focus-visible:!ring-0"
            invalid={!result.ok}
          />
        </Card>
        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">{direction === 'y2j' ? 'JSON' : 'YAML'}</span>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                const ok = await copyToClipboard(result.output);
                if (ok) toast.success('Copied');
              }}
              disabled={!result.ok}
            >
              <Copy size={11} />
            </Button>
          </div>
          <pre className="font-mono text-[13px] leading-relaxed p-4 overflow-auto min-h-[400px] max-h-[480px]">
            {result.ok ? <code>{result.output}</code> : <code className="text-danger">{result.err}</code>}
          </pre>
        </Card>
      </div>
    </div>
  );
}

export default YamlConvert;
